import color from 'atoms/colors/colors';
import shadow from 'atoms/shadows/shadows';
import { fontSize, fontWeight, lineHeight } from 'atoms/typography/typography';

declare module '@mui/material/Button';

const themeOverrides = {
  root: {
    minWidth: 0,
    textTransform: 'capitalize',
    fontWeight: fontWeight.semiBold,
    borderRadius: '8px',
    fontSize: fontSize.textMd,
    lineHeight: lineHeight.textMd,
    padding: '12px 20px',
    height: '48px',
    '@media (max-width: 575px)': {
      padding: '10px 18px',
      height: '44px',
    },
  },
};

export const lightButton = {
  MuiButton: {
    styleOverrides: themeOverrides,
    variants: [
      {
        props: { variant: 'primary' },
        style: {
          backgroundColor: color.green[300],
          color: color.gray[900],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.green[600],
          },
          '&:focused': {
            backgroundColor: color.green[300],
          },
          '&:disabled': {
            backgroundColor: color.green[50],
          },
        },
      },
      {
        props: { variant: 'secondary' },
        style: {
          backgroundColor: color.sunrise[500],
          color: color.gray[900],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.sunrise[500],
          },
          '&:focused': {
            backgroundColor: color.sunrise[200],
          },
          '&:disabled': {
            backgroundColor: color.sunrise[200],
          },
        },
      },
      {
        props: { variant: 'outlined' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.common.white,
          borderColor: color.common.white,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.common.white,
            borderColor: color.common.white,
            color: `${color.gray[900]} !important`,
          },
          '&.focused': {
            backgroundColor: color.common.white,
            boxShadow: `${shadow.xs} ${color.green[50]}`,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'black-outlined' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.common.black,
          borderColor: `${color.common.black} !important`,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.common.black,
            borderColor: color.common.black,
            color: `${color.common.white} !important`,
          },
          '&.focused': {
            backgroundColor: color.common.black,
            boxShadow: `${shadow.xs} ${color.green[50]}`,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'black' },
        style: {
          backgroundColor: color.common.black,
          color: color.common.white,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: '#111',
          },
          '&:focus': {
            backgroundColor: '#222',
          },
          '&:disabled': {
            backgroundColor: '#333',
          },
        },
      },
      {
        props: { variant: 'gray-outlined' },
        style: {
          backgroundColor: color.common.white,
          color: color.common.black,
          borderColor: color.gray[400],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.gray[200],
          },
          '&:focus': {
            backgroundColor: color.gray[200],
          },
          '&:disabled': {
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'navy-outlined' },
        style: {
          backgroundColor: color.common.white,
          color: color.navy[900],
          borderColor: color.navy[900],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.common.white,
            borderColor: color.navy[700],
            color: color.navy[900],
          },
          '&.focused': {
            backgroundColor: color.common.white,
            boxShadow: `${shadow.xs} ${color.green[50]}`,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'link' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.gray[900],
          borderColor: color.common.transparent,
          boxShadow: 'unset',
          padding: 0,
          '&:hover': {
            backgroundColor: color.common.transparent,
            borderColor: color.common.transparent,
            color: color.blue[900],
          },
          '&.focused': {
            backgroundColor: color.common.transparent,
            color: color.gray[900],
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[700],
          },
          '@media (max-width: 575px)': {
            padding: 0,
          },
        },
      },
      {
        props: { variant: 'white' },
        style: {
          backgroundColor: color.common.white,
          color: color.navy[900],
          borderColor: color.common.white,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.gray[200],
            borderColor: color.common.white,
            color: color.gray[900],
          },
          '&.focused': {
            backgroundColor: color.gray[200],
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'white-link' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.common.white,
          borderColor: color.common.transparent,
          boxShadow: 'unset',
          padding: 0,
          '&:hover': {
            backgroundColor: color.common.transparent,
            borderColor: color.common.transparent,
            color: color.green[300],
          },
          '&.focused': {
            backgroundColor: color.common.transparent,
            color: color.common.white,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[700],
          },
          '@media (max-width: 575px)': {
            padding: 0,
          },
        },
      },
    ],
  },
};

export const darkButton = {
  MuiButton: {
    styleOverrides: themeOverrides,
    variants: [
      {
        props: { variant: 'primary' },
        style: {
          backgroundColor: color.green[300],
          color: color.gray[900],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.green[600],
          },
          '&:focused': {
            backgroundColor: color.green[300],
          },
          '&:disabled': {
            backgroundColor: color.green[50],
          },
        },
      },
      {
        props: { variant: 'secondary' },
        style: {
          backgroundColor: color.sunrise[500],
          color: color.gray[900],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.sunrise[500],
          },
          '&:focused': {
            backgroundColor: color.sunrise[200],
          },
          '&:disabled': {
            backgroundColor: color.sunrise[200],
          },
        },
      },
      {
        props: { variant: 'outlined' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.common.white,
          borderColor: color.common.white,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.common.white,
            borderColor: color.common.white,
            color: `${color.gray[900]} !important`,
          },
          '&.focused': {
            backgroundColor: color.common.white,
            boxShadow: `${shadow.xs} ${color.green[50]}`,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'black-outlined' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.common.black,
          borderColor: `${color.common.black} !important`,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.common.black,
            borderColor: color.common.black,
            color: `${color.common.white} !important`,
          },
          '&.focused': {
            backgroundColor: color.common.black,
            boxShadow: `${shadow.xs} ${color.green[50]}`,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'black' },
        style: {
          backgroundColor: color.common.black,
          color: color.common.white,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: '#111',
          },
          '&:focus': {
            backgroundColor: '#222',
          },
          '&:disabled': {
            backgroundColor: '#333',
          },
        },
      },
      {
        props: { variant: 'gray-outlined' },
        style: {
          backgroundColor: color.common.white,
          color: color.common.black,
          borderColor: color.gray[400],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.gray[200],
          },
          '&:focus': {
            backgroundColor: color.gray[200],
          },
          '&:disabled': {
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'navy-outlined' },
        style: {
          backgroundColor: color.common.white,
          color: color.navy[900],
          borderColor: color.navy[900],
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.common.white,
            borderColor: color.navy[700],
            color: color.navy[900],
          },
          '&.focused': {
            backgroundColor: color.common.white,
            boxShadow: `${shadow.xs} ${color.green[50]}`,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'link' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.gray[900],
          borderColor: color.common.transparent,
          boxShadow: 'unset',
          padding: 0,
          '&:hover': {
            backgroundColor: color.common.transparent,
            borderColor: color.common.transparent,
            color: color.blue[900],
          },
          '&.focused': {
            backgroundColor: color.common.transparent,
            color: color.gray[900],
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[700],
          },
          '@media (max-width: 575px)': {
            padding: 0,
          },
        },
      },
      {
        props: { variant: 'white' },
        style: {
          backgroundColor: color.common.white,
          color: color.navy[900],
          borderColor: color.common.white,
          boxShadow: shadow.xs,
          '&:hover': {
            backgroundColor: color.gray[200],
            borderColor: color.common.white,
            color: color.gray[900],
          },
          '&.focused': {
            backgroundColor: color.gray[200],
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[400],
          },
        },
      },
      {
        props: { variant: 'white-link' },
        style: {
          backgroundColor: color.common.transparent,
          color: color.common.white,
          borderColor: color.common.transparent,
          boxShadow: 'unset',
          padding: 0,
          '&:hover': {
            backgroundColor: color.common.transparent,
            borderColor: color.common.transparent,
            color: color.blue[900],
          },
          '&.focused': {
            backgroundColor: color.common.transparent,
            color: color.common.white,
          },
          '&:disabled': {
            backgroundColor: color.common.transparent,
            color: color.gray[700],
          },
          '@media (max-width: 575px)': {
            padding: 0,
          },
        },
      },
    ],
  },
};
